import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PsychicRollDetails } from '../types/psychic';

export interface FuryRollEntry {
  confirmRoll: number;
  confirmTarget: number;
  confirmSuccess: boolean;
  bonusDamage: number;
}

export interface TearingInfoEntry {
  allRolls: number[];
  discardedRoll: number;
  keptRolls: number[];
  finalValue: number;
}

export interface DamageRollEntry {
  title: string;
  dieRoll: number;
  baseDamage: string;
  strengthBonus: number;
  modifiers: { name: string; value: number }[];
  total: number;
  penetration: number;
  hitLocation?: string;
  furyRolls?: FuryRollEntry[];
  wasRerolled?: boolean;
  rerollReason?: string;
  originalRoll?: number;
  tearingInfo?: TearingInfoEntry;
}

export interface AttackModifierEntry {
  name: string;
  value: number;
  isProficiencyPenalty?: boolean;
}

export interface ComprehensiveRollEntry {
  // Attack roll details
  attackRoll: number;
  attackTarget: number;
  attackModifiers: AttackModifierEntry[];
  degrees: number;
  success: boolean;
  hitLocation?: string;
  isJammed?: boolean;

  // Jam details (from JamService)
  jamType?: string;
  jamReason?: string;
  jamClearingAction?: string;

  // Multiple hits (for semi-auto/full-auto)
  totalHits?: number;
  hitLocations?: string[];

  // Damage rolls (one per hit)
  damageRolls: DamageRollEntry[];

  // Total damage summary
  totalDamage: number;
  totalFuryDamage: number;
}

export interface RollEntry {
  id: string;
  timestamp: number;
  type: 'combat' | 'skill' | 'damage' | 'other';
  title: string;
  total: number;
  result: 'success' | 'failure' | 'info';
  details: string;
  relatedRolls?: {
    title: string;
    total: number;
    details: string;
  }[];
  tags?: string[];

  // New comprehensive combat roll data
  combatDetails?: ComprehensiveRollEntry;

  // Reroll tracking
  wasRerolled?: boolean; // True if this roll was superseded by a reroll
  rerolledByEntryId?: string; // ID of the reroll entry that superseded this one
  isReroll?: boolean; // True if this entry is a reroll of another
  originalEntryId?: string; // ID of the original entry this reroll supersedes

  // Psychic roll details
  psychicDetails?: PsychicRollDetails;
}

interface RollHistoryState {
  history: RollEntry[];
  isOpen: boolean;
  addEntry: (entry: Omit<RollEntry, 'id' | 'timestamp'>) => string;
  updateEntry: (id: string, updates: Partial<RollEntry>) => void;
  clearHistory: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
}

export const useRollHistory = create<RollHistoryState>()(
  persist(
    (set) => ({
      history: [],
      isOpen: false,
      addEntry: (entry) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        set((state) => ({
          history: [
            {
              id,
              timestamp: Date.now(),
              ...entry
            },
            ...state.history
          ].slice(0, 100),
          isOpen: true
        }));
        return id;
      },
      updateEntry: (id, updates) =>
        set((state) => ({
          history: state.history.map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  ...updates,
                  relatedRolls: [...(entry.relatedRolls || []), ...(updates.relatedRolls || [])]
                }
              : entry
          )
        })),
      clearHistory: () => set({ history: [] }),
      toggleSidebar: () => set((state) => ({ isOpen: !state.isOpen })),
      setSidebarOpen: (isOpen) => set({ isOpen })
    }),
    {
      name: 'rogue-trader-roll-history'
    }
  )
);
